import { useAuthStore } from '@client/stores/AuthStore';
import { debug } from '@client/utils/logger';

type SessionExpiredListener = () => void;

const listeners = new Set<SessionExpiredListener>();

/**
 * How long a deliberate logout may keep the page alive once its redirect has been attempted.
 *
 * Long enough for the Auth0 /v2/logout round trip and the hop back to /logoutin on a slow
 * connection: the fallback below starts a navigation to /login of its own, which would cancel a
 * logout redirect that is still in flight. Short enough that a tab whose redirect was cancelled
 * tells the user they are signed out instead of leaving them on a page that refuses every request.
 */
export const LOGOUT_REDIRECT_GRACE_MS = 10_000;

let logoutRedirectFallback: ReturnType<typeof setTimeout> | undefined;

/**
 * Registers a callback invoked once per session-expiry transition.
 *
 * Uses a Set so registering the same callback twice is a no-op — safe for
 * module-level subscriptions that may be re-evaluated during HMR or test setup.
 */
export function onSessionExpired(listener: SessionExpiredListener): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

/**
 * Idempotently transitions the client into the expired-session state.
 *
 * Clears the auth token, marks the session expired, and notifies subscribers
 * (e.g. the tRPC query client) so protected pages stop issuing work during the
 * redirect window. Subsequent calls while already expired are no-ops.
 */
export function expireSession(reason: string) {
    const authStore = useAuthStore.getState();

    if (authStore.authTokenExpired) {
        // Load-bearing idempotency guard: listeners such as queryClient.cancelQueries()
        // must only run on the first transition into the expired-session state.
        return;
    }

    if (authStore.isLoggingOut) {
        // A logout in progress is not an expiry. `logout()` clears the token and keeps
        // `authTokenExpired` false on purpose; its fallout must not flip the flag back,
        // broadcast token-expired to other tabs or fire the listeners (CUR-2010).
        return;
    }

    debug(reason);
    authStore.clearAuthToken();
    authStore.setAuthTokenExpired(true);

    for (const listener of listeners) {
        try {
            listener();
        } catch (error) {
            console.error('[sessionExpiry] listener threw:', error);
        }
    }
}

/**
 * Bounds a deliberate logout's redirect window (CUR-2010).
 *
 * While `isLoggingOut` is true this tab refuses protected requests and `expireSession` is a
 * no-op, on the assumption that the browser is about to leave the page. That assumption fails
 * without an error when the navigation is cancelled: a dirty form's "Leave site?" prompt
 * answered with Stay cancels both the Auth0 logout redirect and a receiving tab's move to
 * /login, and nothing throws. Call this once the redirect has been attempted. If the page is
 * still here after `LOGOUT_REDIRECT_GRACE_MS`, the logout is treated as stalled: the flag is
 * cleared and the tab takes the normal expiry path, so `RequireAuth` sends it to /login.
 *
 * Calling it again restarts the grace period from the latest redirect attempt. When the flag
 * has already been cleared (a logout that failed and recovered), the timer does nothing.
 */
export function armLogoutRedirectFallback(): void {
    clearTimeout(logoutRedirectFallback);
    logoutRedirectFallback = setTimeout(() => {
        logoutRedirectFallback = undefined;
        if (!useAuthStore.getState().isLoggingOut) {
            return;
        }

        useAuthStore.setState({ isLoggingOut: false });
        expireSession('[sessionExpiry] Logout redirect did not leave the page, falling back to the expiry path');
    }, LOGOUT_REDIRECT_GRACE_MS);
}
